/**
 * Tiny typed client for the connect-api Worker (served same-origin under /api —
 * the Vite dev proxy forwards to the local Worker on :8789).
 *
 * Admin endpoints are gated by Cloudflare Access at the edge, so in production
 * the browser's Access cookie authenticates the operator and no client-side
 * auth code exists. In LOCAL DEV there is no Access in front of `wrangler
 * dev`, so the Worker accepts an allow-listed `x-admin-email` header instead
 * (same dev-bypass pattern as workers/api). We attach that header on admin
 * calls only when `import.meta.env.DEV` — it is compiled out of production
 * builds entirely.
 */

/* ---------------------------------------------------------------------------
 * Types (mirror the htbh-api response shapes)
 * ------------------------------------------------------------------------- */

export type MessageSource = "qr" | "contact";
/** The contact form's "Please select one" — mirrors the original Weebly form. */
export type MessageTopic = "enquiry" | "feedback" | "eoi" | "other";
export const TOPIC_LABELS: Record<MessageTopic, string> = {
  enquiry: "Enquiry",
  feedback: "Feedback",
  eoi: "Expression of interest",
  other: "Other",
};
export type MessageStatus = "new" | "replied" | "archived";

export interface Message {
  id: number;
  ref: string;
  source: MessageSource;
  topic: MessageTopic;
  name: string | null;
  email: string | null;
  phone: string | null;
  subject: string | null;
  body: string;
  /** 1 when the sender asked for email updates about Connect. */
  wants_updates: number;
  status: MessageStatus;
  created_at: string;
  reply_count: number;
}

export interface MessageReply {
  id: number;
  body: string;
  sent_by: string;
  /** 1 when the reply email was sent, 0 when sending failed (or no address). */
  emailed: number;
  created_at: string;
}

export interface MessageInput {
  source: MessageSource;
  topic?: MessageTopic;
  name?: string;
  email?: string;
  phone?: string;
  subject?: string;
  body: string;
  wants_updates?: boolean;
  /** Cloudflare Turnstile token from the widget on the form. */
  turnstileToken?: string;
  /** Honeypot — real users never fill this; bots do. */
  website?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/* ---------------------------------------------------------------------------
 * Fetch plumbing
 * ------------------------------------------------------------------------- */

/**
 * Dev-only identity for the Worker's `x-admin-email` bypass. Must be on the
 * Worker's admin allow-list (see workers/connect-api .dev.vars). Override with
 * VITE_ADMIN_EMAIL if your local allow-list differs.
 */
const DEV_ADMIN_EMAIL =
  (import.meta.env.VITE_ADMIN_EMAIL as string | undefined) ?? "vc.le21@gmail.com"; // must be in the Worker's ADMIN_EMAILS

/** Pull a human-readable message out of a JSON error body if there is one. */
function errorMessage(status: number, body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const rec = body as Record<string, unknown>;
    for (const key of ["error", "message"]) {
      if (typeof rec[key] === "string" && rec[key] !== "") return rec[key] as string;
    }
  }
  if (typeof body === "string" && body.trim() !== "") return body;
  return `${fallback} (${status})`;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; admin?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.admin && import.meta.env.DEV) headers["x-admin-email"] = DEV_ADMIN_EMAIL;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError(0, "Network error — please check your connection and try again.");
  }

  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = text;
  }

  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(res.status, json, `${method} ${path} failed`));
  }
  return json as T;
}

/* ---------------------------------------------------------------------------
 * Public endpoints
 * ------------------------------------------------------------------------- */

/** POST /api/messages — contact / expression-of-interest submission. */
export function submitMessage(input: MessageInput): Promise<{ ok: boolean; ref: string }> {
  return request("POST", "/api/messages", { body: input });
}

/* ---------------------------------------------------------------------------
 * Admin endpoints (Cloudflare Access in prod; x-admin-email in dev)
 * ------------------------------------------------------------------------- */

/** GET /api/admin/messages?status=… */
export async function adminListMessages(status: MessageStatus): Promise<Message[]> {
  const data = await request<{ messages: Message[] }>(
    "GET",
    `/api/admin/messages?status=${status}`,
    { admin: true },
  );
  return data.messages;
}

/** GET /api/admin/messages/:id */
export function adminGetMessage(
  id: number | string,
): Promise<{ message: Message; replies: MessageReply[] }> {
  return request("GET", `/api/admin/messages/${id}`, { admin: true });
}

/** POST /api/admin/messages/:id/reply */
export function adminReply(
  id: number | string,
  body: string,
): Promise<{ ok: boolean; emailed: boolean }> {
  return request("POST", `/api/admin/messages/${id}/reply`, { body: { body }, admin: true });
}

/** POST /api/admin/messages/:id/archive */
export function adminArchive(id: number | string): Promise<{ ok: boolean }> {
  return request("POST", `/api/admin/messages/${id}/archive`, { admin: true });
}

/** POST /api/admin/messages/:id/reopen */
export function adminReopen(id: number | string): Promise<{ ok: boolean }> {
  return request("POST", `/api/admin/messages/${id}/reopen`, { admin: true });
}
